import React, { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  FormSection,
  IconButton,
} from "@narmi/design_system";
import { useNotificationContext } from "cerulean";
import ReactMarkdown from "react-markdown";

import kebabCase from "lodash.kebabcase";
import { DialogProps, DisplayTokenDialogProps } from "./types";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";

const useCopyText = () => {
  const { sendNotification } = useNotificationContext();
  const { l10n } = useLocalization();
  return {
    copyText: async (text: string) => {
      await navigator.clipboard.writeText(text);
      sendNotification({
        type: "dismissible_success",
        text: l10n.getString("notification-text-copied"),
      });
    },
  };
};

export const getAlertError = (message: string, onDismiss: () => void) => (
  <Alert isActive kind="error" isDismissable onUserDismiss={onDismiss}>
    {message}
  </Alert>
);

export const DisplayOnceTokenDialog = ({
  isOpen,
  onUserDismiss,
  createdToken,
}: DisplayTokenDialogProps) => {
  if (!createdToken) {
    return null;
  }
  const [isMCPDisplay, setIsMCPDisplay] = useState(false);
  const { long_name: fiName } = useInstitutionSettings();
  const { l10n } = useLocalization();

  const { copyText } = useCopyText();
  const serverName = `${kebabCase(fiName)}-online-banking`;
  const mcpConfig = {
    mcpServers: {
      [serverName]: {
        command: "python3",
        args: ["/Users/username/path/to/client.py"],
        env: {
          TOKEN: createdToken?.token,
          SECRET: createdToken?.secret,
          MCP_SERVER_URL: "https://grasshopper-mcp.prd.narmitech.com/mcp/",
        },
      },
    },
  };
  const copyContent = () => {
    if (isMCPDisplay) {
      copyText(JSON.stringify(mcpConfig, null, 2));
    } else {
      copyText(createdToken.token);
    }
  };

  return (
    <Dialog
      title={l10n.getString("dialog-api-token-title")}
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      width="600px"
      footer={
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button onClick={() => setIsMCPDisplay(!isMCPDisplay)}>
            {isMCPDisplay
              ? l10n.getString("button-display-token")
              : l10n.getString("button-display-mcp-config")}
          </Button>
          <Button onClick={copyContent}>
            {isMCPDisplay
              ? l10n.getString("button-copy-mcp-config")
              : l10n.getString("button-copy-token")}
          </Button>
        </div>
      }
    >
      <div style={{ fontSize: "var(--font-size-s)", margin: "10px 0" }}>
        <span>{l10n.getString("dialog-api-token-security-message")}</span>
      </div>
      {isMCPDisplay ? (
        <div className="p-4">
          <pre className="padding--all--xs fontSize--s bgColor--blueGrey rounded--all overflow--scroll">
            <code>{JSON.stringify(mcpConfig, null, 2)}</code>
          </pre>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            fontWeight: "bold",
            color: "var(--color-black)",
          }}
        >
          <FormSection title={l10n.getString("form-section-bearer-token")} />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              backgroundColor: "var(--bgColor-smokeGrey)",
              padding: "10px",
            }}
          >
            <span>{createdToken?.token}</span>
            <IconButton
              name="copy"
              onClick={() => copyText(createdToken?.token || "")}
            />
          </div>
        </div>
      )}
    </Dialog>
  );
};

export const TermsOfUseDialog = ({
  isOpen,
  onUserDismiss,
  onConfirm,
}: DialogProps) => {
  const [hasAgreed, setHasAgreed] = useState(false);
  const [alertEl, setAlertEl] = useState<React.ReactNode>(null);
  const institutionSettings = useInstitutionSettings();
  const { l10n } = useLocalization();

  useEffect(() => {
    setAlertEl(null);
    setHasAgreed(false);
  }, [isOpen]);

  const onConfirmInner = () => {
    setAlertEl(null);
    if (hasAgreed) {
      onConfirm();
    } else {
      setAlertEl(
        getAlertError(l10n.getString("alert-must-accept-terms"), () =>
          setAlertEl(null)
        )
      );
    }
  };

  const dialogFooter = (
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <Button
        style={{ paddingRight: "10px" }}
        kind="plain"
        onClick={onUserDismiss}
      >
        {l10n.getString("button-cancel")}
      </Button>
      <Button onClick={onConfirmInner}>
        {l10n.getString("button-confirm")}
      </Button>
    </div>
  );

  return (
    <Dialog
      title={l10n.getString("dialog-terms-title")}
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      notification={alertEl}
      footer={dialogFooter}
    >
      <div style={{ whiteSpace: "pre-wrap" }}>
        <ReactMarkdown>
          {institutionSettings.api_banking_agreement as string}
        </ReactMarkdown>
      </div>
      <br />
      <div style={{ display: "flex", justifyContent: "flex-start" }}>
        <Checkbox
          label={l10n.getString("checkbox-accept-terms")}
          name="agree"
          checked={hasAgreed}
          onChange={() => setHasAgreed(!hasAgreed)}
        />
      </div>
    </Dialog>
  );
};
